import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { applyDriveHead, DRIVE_THEME_DARK, DRIVE_THEME_LIGHT } from "../../lib/drive/head";
import { useAppTheme } from "../../context/ThemeContext";
import { registerDriveServiceWorker } from "../../lib/drive/sw";
import { getDriveBridge, isNativeDriveApp, nativeDrivePlatform } from "../../lib/drive/bridge";
import DriveInstallPrompt from "./DriveInstallPrompt";
import DriveUpdateBanner from "./DriveUpdateBanner";
import "./drive.css";

/**
 * Contenitore dell'area Drive (/autista, /autista-login): metadati PWA, service worker,
 * invito all'installazione, banner di aggiornamento e integrazione con il wrapper nativo.
 *
 * Contratto con il wrapper nativo (vedi WEBVIEW.md):
 *  - window.DriveWeb.onBack()  -> true se Drive ha gestito il tasto Indietro, false se il
 *    nativo può chiudere l'app (siamo alla radice del giro o al login);
 *  - window.DriveWeb.platform  -> "android" | "ios" | null.
 *  - all'avvio la pagina chiama DriveApp.ready() se esiste.
 */
export default function DriveShell() {
  const location = useLocation();
  const navigate = useNavigate();

  const { effectiveTheme } = useAppTheme();

  useEffect(() => applyDriveHead(), []);

  // theme-color (barra di stato / barra Android) segue il tema effettivo, anche quando cambia
  // quello del sistema con la preferenza "Sistema".
  useEffect(() => {
    const color = effectiveTheme === "dark" ? DRIVE_THEME_DARK : DRIVE_THEME_LIGHT;
    document.head.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute("content", color));
    const b = getDriveBridge();
    if (b && typeof b.setTheme === "function") b.setTheme(effectiveTheme, color); // barre native del wrapper
  }, [effectiveTheme]);
  useEffect(() => {
    if (!isNativeDriveApp()) registerDriveServiceWorker();
  }, []);

  useEffect(() => {
    const atRoot = location.pathname === "/autista" || location.pathname === "/autista-login";
    window.DriveWeb = {
      platform: nativeDrivePlatform(),
      onBack: () => {
        if (atRoot) return false;
        navigate(-1);
        return true;
      },
    };
    return () => {
      delete window.DriveWeb;
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    const b = getDriveBridge();
    if (b && typeof b.ready === "function") b.ready();
  }, []);

  // Tastiera virtuale: porta il campo a fuoco al centro dell'area visibile.
  useEffect(() => {
    const onFocus = (e) => {
      const el = e.target;
      if (!(el instanceof HTMLElement) || !el.matches("input, textarea, select")) return;
      window.setTimeout(() => {
        if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 250);
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, []);

  return (
    <div className="drive-app" data-testid="drive-shell">
      <DriveUpdateBanner />
      <Outlet />
      <DriveInstallPrompt enabled={location.pathname !== "/autista-login"} />
    </div>
  );
}
